// src/components/quizzes/RhythmTapGame.js
//
// "Rhythm Tap" bonus round — rebuilt (Nov 2026). Same name, same id
// (bonusRoundType "rhythmTap"), same contract, so every topic that uses it
// just gets the new game:
//
//     <RhythmTapGame questions placeholderImage onComplete />
//     onComplete(score, { endedEarly })
//
// WHAT CHANGED
// ------------
// The old round was arrows falling to a song they had nothing to do with,
// for the whole round, at one speed. Now it's a real beat game:
//
//   🥁 ITS OWN BEAT. The drums are played by the browser (Web Audio), so
//      every note lands exactly on a beat you can hear, and each pad plays a
//      musical note — tapping in time makes a tune.
//   🎯 PERFECT / GREAT / GOOD. Tap as the note crosses the ring.
//   ❓ ANSWER DROPS. Every few bars the music keeps going, the pads turn into
//      the answers to one of the topic's questions, and the right pad is
//      worth big points (more the faster it's tapped).
//   ⚡ SPEED UP. After each answer drop the beat gets faster (3 levels) and
//      the notes get trickier (off-beats, then two at once).
//   🔥 STREAKS. 10 in a row = ×2, 20 = ×3. 16 in a row starts Fever:
//      one more on the multiplier, and rainbow lanes.
//   🏁 End game, intro card, 3-2-1 and the summary are the same as every
//      other bonus round (kit/bonusShell.css, kit/BonusEndEarly).
//
// Controls: tap the pads, or the arrow keys / A W S D (1-4 for answers).
//
// Everything that moves is a Web Animations API animation, which the browser
// runs on its compositor — taps never make the notes stutter on a phone.

import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { playGameSound } from "../../services/gameSounds";
import BonusEndEarly from "./kit/BonusEndEarly";
import { bonusSay, bonusHush, bonusCanSpeak } from "./kit/bonusVoice";
import "./kit/bonusShell.css";
import "./RhythmTapGame.css";

const LOGO_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f";
const TALK_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Ftalktextbutton.png?alt=media&token=c3a19304-b98a-43cf-9d81-c9b80000375e";
const MUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fmute-button.png?alt=media&token=9b91203e-011f-4324-bbea-300d0d273fcf";
const UNMUTE_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Funmute-button.png?alt=media&token=e6afbbd2-41e5-41c1-b911-513fa41a4360";

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------
const ROUND_SECONDS = 50;
const LEVELS = [
  { bpm: 92, travel: 1900, name: "Level 1" },
  { bpm: 108, travel: 1600, name: "Level 2" },
  { bpm: 124, travel: 1350, name: "Level 3" },
];
const WINDOWS = [
  { ms: 80, label: "PERFECT!", pts: 2, kind: "perfect" },
  { ms: 145, label: "GREAT!", pts: 1, kind: "great" },
  { ms: 215, label: "GOOD", pts: 1, kind: "good" },
];
const FEVER_AT = 16; // notes in a row
const FEVER_BEATS = 16;
const DROP_EVERY_BARS = 4; // an answer drop every 4 bars…
const DROP_FIRST_BAR = 2; // …starting after the 2nd bar
const DROP_BARS = 2; // how long the answers stay up (bars)
// Scores land in the same range as the other bonus rounds (a good round is
// a few hundred), so leaderboards and coins stay fair across games.
const STARS_AT = [100, 250];

const LANES = [
  { id: "left", icon: "◀", color: "#ff5f6d", note: 523.25 }, // C5
  { id: "up", icon: "▲", color: "#ffb938", note: 587.33 }, // D5
  { id: "down", icon: "▼", color: "#3ad38a", note: 659.25 }, // E5
  { id: "right", icon: "▶", color: "#4aa8ff", note: 783.99 }, // G5
];
const KEYS = {
  ArrowLeft: 0, ArrowUp: 1, ArrowDown: 2, ArrowRight: 3,
  a: 0, w: 1, s: 2, d: 3, A: 0, W: 1, S: 2, D: 3,
  1: 0, 2: 1, 3: 2, 4: 3,
};

const INTRO =
  "Rhythm Tap! Tap each note when it reaches the ring at the bottom, right on the beat. When a question drops, tap the pad with the right answer. The more notes in a row, the more points you get!";

let uidN = 0;
const uid = () => `n${Date.now().toString(36)}${(uidN += 1)}`;
const shuffle = (a) => {
  const b = [...a];
  for (let i = b.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [b[i], b[j]] = [b[j], b[i]];
  }
  return b;
};

// The topic's questions, as answer drops (2–4 answers, one right).
const buildDrops = (questions = []) =>
  shuffle(
    (questions || [])
      .map((q) => {
        const answers = (q?.answers || []).filter((a) => a && (a.text || a.image || a.alt));
        const right = answers.find((a) => a.correct);
        if (!q?.question || !right || answers.length < 2) return null;
        const wrong = shuffle(answers.filter((a) => a !== right)).slice(0, 3);
        return {
          question: String(q.question),
          speak: q.speak || q.question,
          answers: shuffle([right, ...wrong]).map((a) => ({ text: a.text || a.alt || "", image: a.image || "", correct: a === right })),
        };
      })
      .filter(Boolean)
  );

// ---------------------------------------------------------------------------
// The beat (Web Audio): kick, snare, hats and the pads' notes
// ---------------------------------------------------------------------------
const makeBeat = () => {
  const AC = typeof window !== "undefined" && (window.AudioContext || window.webkitAudioContext);
  if (!AC) return null;
  let ctx;
  try {
    ctx = new AC();
  } catch {
    return null;
  }
  const master = ctx.createGain();
  master.gain.value = 0.9;
  master.connect(ctx.destination);
  const drums = ctx.createGain();
  drums.gain.value = 0.55;
  drums.connect(master);
  const noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.4, ctx.sampleRate);
  const ch = noiseBuf.getChannelData(0);
  for (let i = 0; i < ch.length; i += 1) ch[i] = Math.random() * 2 - 1;

  const env = (g, t, peak, len) => {
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, t + len);
  };
  const kick = (t) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.setValueAtTime(150, t);
    o.frequency.exponentialRampToValueAtTime(45, t + 0.18);
    env(g, t, 1, 0.3);
    o.connect(g).connect(drums);
    o.start(t);
    o.stop(t + 0.32);
  };
  const noise = (t, { hp = 1000, len = 0.12, peak = 0.5 } = {}) => {
    const s = ctx.createBufferSource();
    s.buffer = noiseBuf;
    const f = ctx.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = hp;
    const g = ctx.createGain();
    env(g, t, peak, len);
    s.connect(f).connect(g).connect(drums);
    s.start(t);
    s.stop(t + len + 0.02);
  };
  const snare = (t) => {
    noise(t, { hp: 1400, len: 0.16, peak: 0.55 });
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(220, t);
    env(g, t, 0.35, 0.1);
    o.connect(g).connect(drums);
    o.start(t);
    o.stop(t + 0.12);
  };
  const hat = (t, open = false) => noise(t, { hp: 7000, len: open ? 0.18 : 0.04, peak: open ? 0.22 : 0.16 });
  const bass = (t, freq, len) => {
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sawtooth";
    o.frequency.setValueAtTime(freq, t);
    const f = ctx.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.value = 520;
    env(g, t, 0.22, len);
    o.connect(f).connect(g).connect(drums);
    o.start(t);
    o.stop(t + len + 0.05);
  };
  // a happy pluck for the pads (and a sparkle on top for Perfect)
  const pluck = (freq, sparkle = false) => {
    if (ctx.state !== "running") return;
    const t = ctx.currentTime + 0.001;
    [1, sparkle ? 2 : 0].filter(Boolean).forEach((m, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = i ? "sine" : "triangle";
      o.frequency.setValueAtTime(freq * m, t);
      env(g, t, i ? 0.12 : 0.3, i ? 0.35 : 0.28);
      o.connect(g).connect(master);
      o.start(t);
      o.stop(t + 0.4);
    });
  };
  const buzz = () => {
    if (ctx.state !== "running") return;
    const t = ctx.currentTime + 0.001;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "square";
    o.frequency.setValueAtTime(140, t);
    o.frequency.linearRampToValueAtTime(90, t + 0.2);
    env(g, t, 0.12, 0.22);
    o.connect(g).connect(master);
    o.start(t);
    o.stop(t + 0.25);
  };
  const BASSLINE = [130.81, 130.81, 174.61, 196.0]; // C C F G, one per bar
  // one beat (and its off-beat) at audio time t
  const beat = (t, i, spb, { fever = false, quiet = false } = {}) => {
    const inBar = i % 4;
    if (inBar === 0 || inBar === 2 || (fever && inBar === 3)) kick(t);
    if (inBar === 1 || inBar === 3) snare(t);
    hat(t);
    hat(t + spb / 2, fever && inBar === 3);
    if (!quiet && inBar === 0) bass(t, BASSLINE[Math.floor(i / 4) % 4], spb * 1.6);
  };
  return {
    ctx,
    resume: () => (ctx.state === "suspended" ? ctx.resume().catch(() => {}) : Promise.resolve()),
    beat,
    pluck,
    buzz,
    setMuted: (m) => master.gain.setTargetAtTime(m ? 0 : 0.9, ctx.currentTime, 0.02),
    setDrumLevel: (v) => drums.gain.setTargetAtTime(v, ctx.currentTime, 0.08),
    close: () => {
      try {
        ctx.close();
      } catch {
        /* ignore */
      }
    },
  };
};

// ---------------------------------------------------------------------------
// One falling note (compositor animation; reports its Animation for pausing)
// ---------------------------------------------------------------------------
const Note = memo(
  ({ note, register, clockNow }) => {
    const ref = useRef(null);
    useEffect(() => {
      const el = ref.current;
      if (!el || typeof el.animate !== "function") return undefined;
      const { fromY, hitY, travel } = note;
      // worked out now, at mount, so a slow render can't make it late; a
      // negative delay simply starts it part-way down
      const delay = note.target - travel - clockNow();
      const total = travel * 1.25;
      const a = el.animate(
        [
          { transform: `translate3d(-50%, ${fromY}px, 0) scale(.85)`, opacity: 0 },
          { opacity: 1, offset: 0.08 },
          { transform: `translate3d(-50%, ${hitY}px, 0) scale(1)`, offset: 0.8 },
          { transform: `translate3d(-50%, ${hitY + (hitY - fromY) * 0.25}px, 0) scale(1)` },
        ],
        { duration: total, delay, easing: "linear", fill: "both" }
      );
      register(note.id, a);
      return () => {
        register(note.id, null);
        try {
          a.cancel();
        } catch {
          /* ignore */
        }
      };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    return (
      <div ref={ref} className={`rt-note ${note.fever ? "is-fever" : ""}`} style={{ "--c": LANES[note.lane].color }}>
        <span>♪</span>
      </div>
    );
  },
  (a, b) => a.note === b.note
);

// ---------------------------------------------------------------------------
// The game
// ---------------------------------------------------------------------------
const RhythmTapGame = ({ questions = [], placeholderImage, onComplete, onBonusComplete }) => {
  const finishCb = onComplete || onBonusComplete;
  const [phase, setPhase] = useState("intro"); // intro | count | play | done
  const [countdown, setCountdown] = useState(null);
  const [score, setScore] = useState(0);
  const [timer, setTimer] = useState(ROUND_SECONDS);
  const [notes, setNotes] = useState([]);
  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [hits, setHits] = useState(0);
  const [perfects, setPerfects] = useState(0);
  const [rightDrops, setRightDrops] = useState(0);
  const [level, setLevel] = useState(0);
  const [fever, setFever] = useState(false);
  const [drop, setDrop] = useState(null); // { ...question, endsAt, picked }
  const [judge, setJudge] = useState(null); // { id, lane, label, kind }
  const [flash, setFlash] = useState(null);
  const [padHit, setPadHit] = useState({}); // lane -> "good" | "bad"
  const [paused, setPaused] = useState(false);
  const [endedEarly, setEndedEarly] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);

  const drops = useRef(buildDrops(questions));
  const dropIdx = useRef(0);
  const beat = useRef(null); // the Web Audio beat
  const stageRef = useRef(null);
  const lanesRef = useRef(null);
  const live = useRef(new Map()); // note id -> { lane, target, hit }
  const anims = useRef(new Map()); // note id -> Animation
  const clock = useRef({ pausedAt: 0, pausedTotal: 0 });
  const sched = useRef(null); // the running schedule
  const scoreRef = useRef(0);
  const comboRef = useRef(0);
  const feverRef = useRef(0); // beats of fever left
  const levelRef = useRef(0);
  const dropRef = useRef(null);
  const pausedRef = useRef(false);
  const sentRef = useRef(false);
  scoreRef.current = score;
  comboRef.current = combo;
  levelRef.current = level;
  dropRef.current = drop;
  pausedRef.current = paused;

  const now = () => performance.now() - clock.current.pausedTotal;
  const clockNow = useCallback(() => performance.now() - clock.current.pausedTotal, []);
  const padRef = useRef(null);

  // ---- voice ----
  const say = useCallback((text) => {
    bonusSay(text, {
      onStart: () => {
        setSpeaking(true);
        beat.current?.setDrumLevel(0.18);
      },
      onEnd: () => {
        setSpeaking(false);
        beat.current?.setDrumLevel(0.55);
      },
    });
  }, []);
  const hush = useCallback(() => {
    bonusHush();
    setSpeaking(false);
    beat.current?.setDrumLevel(0.55);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => say(INTRO), 150);
    return () => {
      clearTimeout(t);
      bonusHush();
      if (sched.current) clearInterval(sched.current.timer);
      beat.current?.close();
    };
  }, [say]);

  // ---- flow ----
  const begin = () => {
    hush();
    if (!beat.current) beat.current = makeBeat();
    beat.current?.resume(); // inside the tap, so phones allow sound
    setPhase("count");
    setCountdown(3);
  };

  useEffect(() => {
    if (countdown === null) return undefined;
    if (countdown === "GO!") {
      const t = setTimeout(() => {
        setCountdown(null);
        setPhase("play");
      }, 550);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCountdown((c) => (c > 1 ? c - 1 : "GO!")), 700);
    return () => clearTimeout(t);
  }, [countdown]);

  const showFlash = (text, small = false) => {
    setFlash({ text, small, id: uid() });
    setTimeout(() => setFlash(null), 1100);
  };

  const addScore = (n) => setScore((s) => Math.max(0, s + n));
  const multiplier = (c = comboRef.current) => 1 + Math.min(2, Math.floor(c / 10)) + (feverRef.current > 0 ? 1 : 0);

  const lightPad = (lane, kind) => {
    setPadHit((p) => ({ ...p, [lane]: kind }));
    setTimeout(() => setPadHit((p) => (p[lane] === kind ? { ...p, [lane]: null } : p)), 160);
  };

  const pulseStage = () => {
    const el = stageRef.current;
    if (el && el.animate) el.animate([{ transform: "scale(1.012)" }, { transform: "scale(1)" }], { duration: 160, easing: "ease-out" });
  };

  const removeNote = (id) => {
    live.current.delete(id);
    setNotes((prev) => prev.filter((n) => n.id !== id));
  };

  const breakCombo = () => {
    setCombo(0);
    comboRef.current = 0;
  };

  // ---- the schedule: beats (audio), notes (visual), drops ----
  // Builds the notes for one bar at this level: quarters, then off-beats,
  // then two at once.
  const patternFor = (lvl) => {
    const steps = []; // [eighthIndex, lane]
    let last = -1;
    const pickLane = () => {
      let l = Math.floor(Math.random() * 4);
      if (l === last && Math.random() < 0.6) l = (l + 1 + Math.floor(Math.random() * 3)) % 4;
      last = l;
      return l;
    };
    for (let e = 0; e < 8; e += 1) {
      const onBeat = e % 2 === 0;
      let p = 0;
      if (lvl === 0) p = onBeat ? 0.8 : 0;
      if (lvl === 1) p = onBeat ? 0.85 : 0.3;
      if (lvl === 2) p = onBeat ? 0.9 : 0.45;
      if (Math.random() < p) {
        const l = pickLane();
        steps.push([e, l]);
        if (lvl === 2 && onBeat && Math.random() < 0.18) steps.push([e, (l + 2) % 4]);
      }
    }
    if (!steps.length) steps.push([0, pickLane()]);
    return steps;
  };

  const startSchedule = () => {
    const start = now() + 400;
    const s = {
      start,
      endAt: start + ROUND_SECONDS * 1000,
      bars: [], // { start, spb } for every bar written so far
      noteBarTime: start, // when the next bar to write starts
      audioBar: 0, // next beat to play: bar…
      audioBeat: 0, // …and beat in it
      barsSinceDrop: 0,
      quietUntilBar: -1, // no notes while an answer drop is up
      timer: null,
    };
    sched.current = s;
    s.timer = setInterval(() => tick(s), 25); // eslint-disable-line no-use-before-define
  };

  // where the ring is, measured from the top of a lane
  const hitLine = () => {
    const pad = padRef.current;
    if (pad) return pad.offsetTop + pad.offsetHeight / 2;
    const h = lanesRef.current ? lanesRef.current.clientHeight : 500;
    return h - 90;
  };

  const tick = (s) => {
    if (pausedRef.current || sched.current !== s) return;
    const t = now();
    const b = beat.current;

    // 1) write each bar (its tempo, its notes, maybe an answer drop) as soon
    //    as its first note has to start falling. A speed-up only changes
    //    bars not written yet, so the beat and the notes always agree.
    for (;;) {
      const lvl = LEVELS[levelRef.current];
      const spb = 60000 / lvl.bpm;
      if (!(s.noteBarTime - lvl.travel < t + 120 && s.noteBarTime < s.endAt)) break;
      const barStart = s.noteBarTime;
      const barNo = s.bars.length;
      s.bars.push({ start: barStart, spb });
      const wantDrop =
        drops.current.length > 0 && barNo >= DROP_FIRST_BAR && s.barsSinceDrop >= DROP_EVERY_BARS && barStart + spb * 4 * DROP_BARS < s.endAt;
      if (wantDrop) {
        s.barsSinceDrop = 0;
        s.quietUntilBar = barNo + DROP_BARS - 1;
        const q = drops.current[dropIdx.current % drops.current.length];
        dropIdx.current += 1;
        const endsAt = barStart + spb * 4 * DROP_BARS;
        setTimeout(() => {
          if (sched.current !== s) return;
          // eslint-disable-next-line no-use-before-define
          openDrop({ ...q, endsAt, opensAt: barStart, id: uid() });
        }, Math.max(0, barStart - t));
      } else {
        s.barsSinceDrop += 1;
      }
      if (barNo > s.quietUntilBar) {
        const hitY = hitLine();
        const fresh = patternFor(levelRef.current)
          .map(([e, lane]) => ({ id: uid(), lane, target: barStart + e * (spb / 2), travel: lvl.travel, fever: feverRef.current > 0, fromY: -70, hitY }))
          .filter((n) => n.target < s.endAt - 150);
        fresh.forEach((n) => live.current.set(n.id, { lane: n.lane, target: n.target, hit: false }));
        if (fresh.length) setNotes((prev) => [...prev, ...fresh]);
      }
      s.noteBarTime += spb * 4;
    }

    // 2) the beat: play what falls in the next 150 ms
    while (s.audioBar < s.bars.length) {
      const bar = s.bars[s.audioBar];
      const bt = bar.start + s.audioBeat * bar.spb;
      if (bt >= t + 150 || bt >= s.endAt) break;
      const delay = Math.max(0, bt - t);
      if (b && b.ctx.state === "running") {
        b.beat(b.ctx.currentTime + delay / 1000, s.audioBar * 4 + s.audioBeat, bar.spb / 1000, {
          fever: feverRef.current > 0,
          quiet: Boolean(dropRef.current),
        });
      }
      setTimeout(pulseStage, delay);
      if (feverRef.current > 0) {
        feverRef.current -= 1;
        if (feverRef.current === 0) setTimeout(() => setFever(false), delay);
      }
      s.audioBeat += 1;
      if (s.audioBeat === 4) {
        s.audioBeat = 0;
        s.audioBar += 1;
      }
    }

    // 3) misses: notes that went past the ring
    live.current.forEach((n, id) => {
      if (!n.hit && t > n.target + WINDOWS[WINDOWS.length - 1].ms) {
        n.hit = true;
        if (comboRef.current > 0) breakCombo();
        setTimeout(() => removeNote(id), 260);
      }
    });

    // 4) the answer drop ran out
    const d = dropRef.current;
    if (d && !d.picked && t > d.endsAt) {
      // eslint-disable-next-line no-use-before-define
      closeDrop(d, null);
    }

    // 5) the clock
    const left = Math.max(0, Math.ceil((s.endAt - t) / 1000));
    setTimer((old) => (old === left ? old : left));
    if (t >= s.endAt + 300) {
      clearInterval(s.timer);
      setPhase("done");
    }
  };

  useEffect(() => {
    if (phase !== "play") return undefined;
    startSchedule();
    return () => {
      if (sched.current) clearInterval(sched.current.timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // ---- answer drops ----
  const openDrop = (d) => {
    setDrop(d);
    dropRef.current = d;
    beat.current?.setDrumLevel(0.3);
    say(d.speak);
  };

  // faster from the next bar that gets written (see tick, step 1)
  const levelUp = () => {
    if (levelRef.current >= LEVELS.length - 1) return;
    const next = levelRef.current + 1;
    levelRef.current = next;
    setLevel(next);
    setTimeout(() => showFlash(`⚡ SPEED UP! ${LEVELS[next].name}`, true), 300);
  };

  const closeDrop = (d, pickedIdx) => {
    const right = pickedIdx !== null && d.answers[pickedIdx]?.correct;
    const shown = { ...d, picked: true, pickedIdx, right };
    setDrop(shown);
    dropRef.current = shown;
    beat.current?.setDrumLevel(0.55);
    if (right) {
      const t = now();
      const span = Math.max(1, d.endsAt - d.opensAt);
      const quick = Math.max(0, Math.min(1, (d.endsAt - t) / span));
      const pts = Math.round((10 + 10 * quick) * multiplier());
      addScore(pts);
      setRightDrops((n) => n + 1);
      setCombo((c) => c + 4);
      comboRef.current += 4;
      setBestCombo((b) => Math.max(b, comboRef.current));
      setJudge({ id: uid(), lane: pickedIdx, label: `+${pts}`, kind: "drop" });
      playGameSound("bonusPower", "rhythmTap", () => beat.current?.pluck(1046.5, true));
      say("Yes!");
      confetti({ spread: 70, particleCount: 50, startVelocity: 38, origin: { y: 0.55 } });
    } else {
      playGameSound("bonusWrong", "rhythmTap", () => beat.current?.buzz());
      breakCombo();
      if (pickedIdx !== null) setJudge({ id: uid(), lane: pickedIdx, label: "Oops!", kind: "miss" });
      const rightOne = d.answers.find((a) => a.correct);
      say(rightOne?.text ? `It was ${rightOne.text}.` : "Good try.");
    }
    setTimeout(() => {
      setDrop((cur) => (cur && cur.id === d.id ? null : cur));
      if (dropRef.current && dropRef.current.id === d.id) dropRef.current = null;
      levelUp();
    }, 1300);
  };

  // ---- tapping ----
  const press = (lane) => {
    if (phase !== "play" || pausedRef.current) return;
    const d = dropRef.current;
    if (d && !d.picked) {
      if (lane < d.answers.length) closeDrop(d, lane);
      return;
    }
    const t = now();
    // the closest note in this lane that hasn't been hit
    let best = null;
    live.current.forEach((n, id) => {
      if (n.hit || n.lane !== lane) return;
      const off = Math.abs(t - n.target);
      if (!best || off < best.off) best = { id, n, off };
    });
    const win = best ? WINDOWS.find((w) => best.off <= w.ms) : null;
    if (!best || !win) {
      // tapped with nothing there: no points lost, but the streak resets
      beat.current?.pluck(LANES[lane].note / 2);
      lightPad(lane, "bad");
      if (best && best.off < 400) {
        breakCombo();
        setJudge({ id: uid(), lane, label: "Too soon", kind: "miss" });
      }
      return;
    }
    best.n.hit = true;
    removeNote(best.id);
    const c = comboRef.current + 1;
    comboRef.current = c;
    setCombo(c);
    setBestCombo((b) => Math.max(b, c));
    setHits((h) => h + 1);
    if (win.kind === "perfect") setPerfects((p) => p + 1);
    const pts = win.pts * multiplier(c);
    addScore(pts);
    setJudge({ id: uid(), lane, label: win.label, kind: win.kind, pts });
    lightPad(lane, "good");
    beat.current?.pluck(LANES[lane].note, win.kind === "perfect");
    if (c === FEVER_AT || (c > FEVER_AT && c % FEVER_AT === 0 && feverRef.current === 0)) {
      feverRef.current = FEVER_BEATS;
      setFever(true);
      showFlash("🔥 FEVER!");
      playGameSound("bonusPower", "rhythmTap", () => beat.current?.pluck(1046.5, true));
    } else if (c % 10 === 0) {
      showFlash(`${c} in a row!`, true);
    }
  };
  const pressRef = useRef(press);
  pressRef.current = press;

  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat) return;
      const lane = KEYS[e.key];
      if (lane === undefined) return;
      e.preventDefault();
      pressRef.current(lane);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ---- pause (the End game question) ----
  useEffect(() => {
    if (phase !== "play") return;
    if (paused) {
      clock.current.pausedAt = performance.now();
      anims.current.forEach((a) => {
        try {
          a.pause();
        } catch {
          /* ignore */
        }
      });
      beat.current?.ctx.suspend?.().catch(() => {});
    } else if (clock.current.pausedAt) {
      clock.current.pausedTotal += performance.now() - clock.current.pausedAt;
      clock.current.pausedAt = 0;
      anims.current.forEach((a) => {
        try {
          a.play();
        } catch {
          /* ignore */
        }
      });
      beat.current?.resume();
    }
  }, [paused, phase]);

  const register = useCallback((id, a) => {
    if (a) anims.current.set(id, a);
    else anims.current.delete(id);
  }, []);

  // ---- the end ----
  const send = useCallback(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    hush();
    beat.current?.close();
    finishCb?.(Math.max(0, scoreRef.current), { endedEarly });
  }, [finishCb, endedEarly, hush]);

  useEffect(() => {
    if (phase !== "done") return undefined;
    if (sched.current) clearInterval(sched.current.timer);
    sched.current = null;
    setNotes([]);
    live.current.clear();
    setDrop(null);
    beat.current?.setDrumLevel(0);
    if (scoreRef.current > 0 && !endedEarly) {
      confetti({ spread: 100, particleCount: 120, startVelocity: 50, origin: { y: 0.5 } });
      
    }
    const t = setTimeout(send, endedEarly ? 4200 : 4000);
    return () => clearTimeout(t);
  }, [phase, endedEarly, send]);

  const endEarly = () => {
    setEndedEarly(true);
    setPhase("done");
  };

  // ---- drawing ----
  const stars = endedEarly ? 0 : score >= STARS_AT[1] ? 3 : score >= STARS_AT[0] ? 2 : score > 0 ? 1 : 0;
  const mult = 1 + Math.min(2, Math.floor(combo / 10)) + (fever ? 1 : 0);
  const lowTime = phase === "play" && timer <= 5;
  const hasDrops = drops.current.length > 0;
  const bg = placeholderImage;

  return (
    <>
      {phase === "intro" && (
        <div className="bs-overlay">
          <div className="bs-panel">
            <div className="bs-title">🥁 Rhythm Tap</div>
            <div className="bs-sub">Tap to the beat — and answer to the beat!</div>
            <div className="bs-key">
              <div className="bs-key-row">
                <span className="bs-sw">🎯</span>
                <span>Tap a note as it hits the ring (or use the arrow keys)</span>
                <span className="bs-pts">+2</span>
              </div>
              <div className="bs-key-row">
                <span className="bs-sw">✨</span>
                <span>Right on the beat = PERFECT</span>
              </div>
              {hasDrops && (
                <div className="bs-key-row">
                  <span className="bs-sw">❓</span>
                  <span>Answer drop — tap the pad with the right answer, fast!</span>
                  <span className="bs-pts">+20</span>
                </div>
              )}
              <div className="bs-key-row">
                <span className="bs-sw">⚡</span>
                <span>{hasDrops ? "Every answer drop speeds the beat up" : "The beat speeds up as you go"}</span>
              </div>
              <div className="bs-key-row">
                <span className="bs-sw">🔥</span>
                <span>10 in a row = ×2, 20 = ×3 — and 16 starts FEVER: +1 more!</span>
              </div>
            </div>
            <div className="bs-actions">
              {bonusCanSpeak() && (
                <button type="button" className="bs-talk" onClick={() => say(INTRO)} title="Read the directions" aria-label="Read the directions out loud">
                  <img src={TALK_URL} alt="Talk text" />
                </button>
              )}
              {speaking && (
                <button type="button" className="bs-stop" onClick={hush}>
                  ⏹ Stop
                </button>
              )}
              <button type="button" className="bs-start" onClick={begin}>
                Start!
              </button>
            </div>
          </div>
        </div>
      )}

      {countdown !== null && (
        <div className="bs-count">
          <span key={String(countdown)}>{countdown}</span>
        </div>
      )}

      <div className={`bs-root rt-root ${fever ? "is-fever" : ""}`} style={bg ? { backgroundImage: `url('${bg}')` } : undefined}>
        <div className="rt-glow" />
        <div className="bs-hud">
          <div className="bs-card">
            <span className="bs-label">Score</span>
            <div className="bs-value">{score}</div>
          </div>
          <div className="bs-card" style={{ textAlign: "right" }}>
            <span className="bs-label">Time</span>
            <div className={`bs-value ${lowTime ? "is-urgent" : ""}`}>{timer}s</div>
            <div className="bs-track">
              <div className={`bs-fill ${lowTime ? "is-urgent" : ""}`} style={{ width: `${Math.min(100, (timer / ROUND_SECONDS) * 100)}%` }} />
            </div>
          </div>
        </div>

        {!drop && <img src={LOGO_URL} alt="SBL Logo" className="bs-logo" />}

        {!drop && (
          <div className="rt-meter" aria-hidden="true">
            <span className="rt-level">{LEVELS[level].name}</span>
            <span className="rt-combo">
              🔥 {combo}
              {mult > 1 && <b>×{mult}</b>}
            </span>
          </div>
        )}

        {drop && (
          <div key={drop.id} className={`rt-drop ${drop.picked ? (drop.right ? "is-right" : "is-wrong") : ""}`} aria-live="polite">
            <div className="rt-drop-tag">❓ Answer drop!</div>
            <div className="rt-drop-q">{drop.question}</div>
            {!drop.picked && (
              <div className="rt-drop-timer">
                <div style={{ animationDuration: `${Math.max(500, drop.endsAt - drop.opensAt)}ms` }} />
              </div>
            )}
          </div>
        )}

        {flash && (
          <div key={flash.id} className={`bs-flash ${flash.small ? "is-small" : ""}`}>
            {flash.text}
          </div>
        )}

        <div className="rt-stage" ref={stageRef}>
          <div className="rt-lanes" ref={lanesRef}>
            {LANES.map((L, i) => {
              const ans = drop && drop.answers[i];
              const hit = padHit[i];
              let padState = "";
              if (drop && drop.picked && ans) {
                if (ans.correct) padState = "is-answer-right";
                else if (drop.pickedIdx === i) padState = "is-answer-wrong";
              }
              return (
                <div key={L.id} className={`rt-lane ${drop ? "is-drop" : ""} ${drop && !ans ? "is-off" : ""}`} style={{ "--c": L.color }}>
                  <div className="rt-rail" />
                  {notes
                    .filter((n) => n.lane === i)
                    .map((n) => (
                      <Note key={n.id} note={n} register={register} clockNow={clockNow} />
                    ))}
                  {judge && judge.lane === i && (
                    <div key={judge.id} className={`rt-judge is-${judge.kind}`}>
                      {judge.label}
                    </div>
                  )}
                  <button
                    type="button"
                    ref={i === 0 ? padRef : undefined}
                    className={`rt-pad ${hit ? `is-${hit}` : ""} ${ans ? "has-answer" : ""} ${padState}`}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      press(i);
                    }}
                    aria-label={ans ? `Answer ${i + 1}: ${ans.text}` : `${L.id} pad`}
                  >
                    {ans ? (
                      <span className="rt-ans">
                        {ans.image && <img src={ans.image} alt="" draggable="false" />}
                        {ans.text && <span>{ans.text}</span>}
                      </span>
                    ) : (
                      <span className="rt-icon">{L.icon}</span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          className="bs-mute"
          onClick={() => {
            const next = !muted;
            setMuted(next);
            beat.current?.setMuted(next);
          }}
          aria-label={muted ? "Turn the beat on" : "Turn the beat off"}
        >
          <img src={muted ? UNMUTE_URL : MUTE_URL} alt="" />
        </button>
      </div>

      {(phase === "play" || phase === "count") && <BonusEndEarly onEnd={endEarly} onPause={setPaused} />}

      {phase === "done" && (
        <div className="bs-overlay is-summary">
          <div className="bs-panel">
            <div className="bs-title">{endedEarly ? "Round ended early" : "Bonus Round Complete!"}</div>
            {!endedEarly && (
              <div className="bs-stars" aria-label={`${stars} out of 3 stars`}>
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`bs-star ${i < stars ? "" : "is-dim"}`}>
                    ⭐
                  </span>
                ))}
              </div>
            )}
            <div className="bs-score-label">Bonus Points</div>
            <div className="bs-score">{score}</div>
            <div className="bs-stats">
              <div className="bs-stat">
                <div className="n">{hits}</div>
                <div className="k">Notes hit</div>
              </div>
              <div className="bs-stat">
                <div className="n">{perfects}</div>
                <div className="k">Perfects</div>
              </div>
              {hasDrops && (
                <div className="bs-stat">
                  <div className="n">{rightDrops}</div>
                  <div className="k">Answers right</div>
                </div>
              )}
              <div className="bs-stat">
                <div className="n">{bestCombo}</div>
                <div className="k">Best streak</div>
              </div>
            </div>
            {endedEarly && <p className="bs-early-note">Your points still count! Play the whole bonus round next time to earn the ⭐ star.</p>}
            <button type="button" className="bs-continue" onClick={send}>
              Continue ▶
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default RhythmTapGame;
